import { useMemo, useState } from 'react';
import { DataGrid, type GridColumn } from '../../engine/views/DataGrid';
import { Field, Select } from '../../engine/design/primitives/Input';
import { DatePicker } from '../../engine/design/primitives/DatePicker';
import { EmptyState, Progress } from '../../engine/design/primitives/Misc';
import { formatDate, formatMinutes } from '../../lib/format';
import { addDays, todayIso, useWorkload, weekStart, type WorkloadCell, type WorkloadTech } from './data';

// Workload: technicians × weeks, each cell the allocated minutes of open tasks against the
// technician's weekly capacity (v_technician_workload). Rendered with the engine DataGrid;
// the week columns are generated from the API's `weeks`.

function Cell({ c }: { c: WorkloadCell }) {
  const over = c.load > 1;
  return (
    <span className={['cell', over && 'over'].filter(Boolean).join(' ')} title={`${c.task_count} task(s) · ${formatMinutes(c.allocated_minutes)} of ${formatMinutes(c.capacity_minutes)}`}>
      <Progress value={c.load} tone={over ? 'neg' : c.load > 0.85 ? 'warn' : undefined} title={`${Math.round(c.load * 100)}% booked`} />
      <span className="nums">
        {formatMinutes(c.allocated_minutes)} <span className="hf-muted">/ {formatMinutes(c.capacity_minutes)}</span>
      </span>
    </span>
  );
}

export function WorkloadGrid({ techIds, initialFrom, embedded }: { techIds?: string[]; initialFrom?: string; embedded?: boolean }) {
  const [from, setFrom] = useState(weekStart(initialFrom ?? todayIso()));
  const [weeks, setWeeks] = useState(8);
  const to = addDays(from, weeks * 7 - 1);
  const q = useWorkload(from, to);

  const rows = useMemo(() => {
    const techs = q.data?.technicians ?? [];
    return techIds ? techs.filter((t) => techIds.includes(t.id)) : techs;
  }, [q.data, techIds]);

  const columns = useMemo<GridColumn<WorkloadTech>[]>(() => {
    const weekCols: GridColumn<WorkloadTech>[] = (q.data?.weeks ?? []).map((w, i) => ({
      id: w,
      header: `w/c ${formatDate(w)}`,
      width: 150,
      align: 'center',
      cell: (r) => (r.weeks[i] ? <Cell c={r.weeks[i]!} /> : null),
    }));
    return [
      { id: 'name', header: 'Technician', width: 200, cell: (r) => <span>{r.name}</span> },
      ...weekCols,
      { id: 'total', header: 'Total', width: 110, align: 'right', cell: (r) => <span className="hf-num">{formatMinutes(r.total_allocated_minutes)}</span> },
    ];
  }, [q.data?.weeks]);

  return (
    <div className="hf-prj-workload">
      <div className="hf-prj-tools">
        <div className="hf-row">
          <Field label="From" inline>
            <DatePicker size="sm" value={from} onChange={(iso) => iso && setFrom(weekStart(iso))} aria-label="From week" />
          </Field>
          <Field label="Weeks" inline>
            <Select size="sm" aria-label="Weeks" value={String(weeks)} onChange={(e) => setWeeks(Number(e.target.value))} options={[4, 8, 12, 16].map((n) => ({ value: String(n), label: `${n} weeks` }))} />
          </Field>
        </div>
        <span className="hf-muted">Remaining estimate of open tasks, spread over each task's weeks, against weekly capacity.</span>
      </div>
      <DataGrid<WorkloadTech>
        aria-label="Workload"
        columns={columns}
        rows={rows}
        keyOf={(r) => r.id}
        loading={q.isLoading}
        error={q.error}
        hideToolbar
        height={embedded ? 320 : undefined}
        empty={<EmptyState title="No technicians" icon="users" hint="Active technicians appear here with their weekly capacity." />}
      />
    </div>
  );
}
